import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ArrowLeft,
    Send,
    Sparkles,
    Trash2,
    MessageCircle,
    ExternalLink
} from 'lucide-react';
import { TertiaryButton, IconButton } from '../components/brand/BrandButton';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import Container from '../components/brand/BrandLayout';
import { CoRegulateMark } from '../components/Logo';
import { parseMessageWithActions } from '../utils/actionTags';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Tool link component - subtle inline style
const ToolLink = ({ action, onClick }) => (
    <button
        onClick={onClick}
        className="inline-flex items-center gap-1 text-[#1D6B6B] hover:text-[#155555] 
                   underline underline-offset-2 decoration-[#1D6B6B]/40 hover:decoration-[#1D6B6B]
                   transition-colors text-sm"
        data-testid={`tool-link-${action.tag.toLowerCase()}`}
    >
        {action.name}
        <ExternalLink className="w-3 h-3" />
    </button>
);

/**
 * AI Guide - A calm, grounded companion
 * 
 * Supports users with warmth, clarity, and gentle pacing.
 * Understands ADHD nervous systems and avoids overwhelm.
 */

const AIGuide = () => {
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);
    const [sessionId, setSessionId] = useState(null);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Generate or retrieve session ID and load history
    useEffect(() => {
        const initSession = async () => {
            setIsLoadingHistory(true);
            try {
                let sid = localStorage.getItem('coregulate_guide_session');
                
                if (!sid) {
                    sid = crypto.randomUUID();
                    localStorage.setItem('coregulate_guide_session', sid);
                }
                
                setSessionId(sid);
                
                // Load history for this session
                const token = localStorage.getItem('co_regulate_token');
                const response = await fetch(`${API_URL}/api/guide/history/${sid}`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.messages && data.messages.length > 0) {
                        setMessages(data.messages);
                        console.log(`Restored ${data.messages.length} messages from session ${sid}`);
                    }
                }
            } catch (error) {
                console.error('Failed to initialize session or load history:', error);
            } finally {
                setIsLoadingHistory(false);
            }
        };
        
        initSession();
    }, []);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Check if user has completed assessments
    const hasCompletedInitialAssessment = () => {
        return localStorage.getItem('coregulate_initial_assessment_complete') === 'true';
    };

    const hasCompletedRelationshipAssessment = () => {
        return localStorage.getItem('coregulate_relationship_assessment_complete') === 'true';
    };

    const sendMessage = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage = {
            role: 'user',
            content: input.trim(),
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/guide/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({
                    message: userMessage.content,
                    session_id: sessionId,
                    has_completed_assessment: hasCompletedInitialAssessment(),
                    has_completed_relationship_assessment: hasCompletedRelationshipAssessment()
                })
            });

            if (response.ok) {
                const data = await response.json();
                
                // Update session ID if it changed
                if (data.session_id && data.session_id !== sessionId) {
                    setSessionId(data.session_id);
                    localStorage.setItem('coregulate_guide_session', data.session_id);
                }

                const assistantMessage = {
                    role: 'assistant',
                    content: data.response,
                    timestamp: new Date().toISOString(),
                    action: data.action
                };

                setMessages(prev => [...prev, assistantMessage]);

                // Handle navigation actions after a short delay
                const actionRoutes = {
                    'start_initial_assessment': '/initial-assessment',
                    'go_to_check_in': '/initial-assessment',
                    'go_to_assessment': '/initial-assessment',
                    'start_relationship_assessment': '/relationship-hub/check-in',
                    'go_to_still_sphere': '/regulate/still-sphere',
                    'go_to_calm_cycle': '/regulate/breathing-orb',
                    'go_to_micro_reset': '/regulate/micro-reset',
                    'go_to_micro_reset_breath': '/regulate/micro-reset?focus=breath',
                    'go_to_micro_reset_somatic': '/regulate/micro-reset?focus=somatic',
                    'go_to_micro_reset_grounding': '/regulate/micro-reset?focus=grounding',
                    'go_to_micro_reset_movement': '/regulate/micro-reset?focus=movement',
                    'go_to_micro_reset_mindful': '/regulate/micro-reset?focus=mindful',
                    'go_to_box_breathing': '/regulate/box-breathing',
                    'go_to_navigator': '/regulate/navigator',
                    'go_to_regulation_tools': '/regulate',
                    'go_to_hypnotherapy': '/hypnotherapy',
                    'go_to_guided_meditations': '/regulate/guided-meditations',
                    'go_to_rsd_meditation': '/regulate/guided-meditations?meditation=rsd-healing',
                    'go_to_calming_anxiety_meditation': '/regulate/guided-meditations?meditation=calming-anxiety',
                    'go_to_emdr': '/regulate/emdr',
                    'go_to_emdr_visual': '/regulate/emdr?mode=visual',
                    'go_to_emdr_audio': '/regulate/emdr?mode=audio',
                    // Relationship Hub tools
                    'go_to_relationship_hub': '/relationship-hub',
                    'go_to_relationship_check_in': '/relationship-hub/check-in',
                    'go_to_break_the_cycle': '/relationship-hub/break-the-cycle',
                    'go_to_which_cycle': '/relationship-hub/cycles',
                    'go_to_attachment_needs': '/relationship-hub/attachment-needs',
                    'go_to_solo_need': '/relationship-hub/solo-need',
                    'go_to_repair': '/relationship-hub/repair',
                    'go_to_relationship_safety': '/regulate/relationship-check',
                    'go_to_coregulation': '/regulate/coregulation',
                    'go_to_partner_mode': '/regulate/partner-mode',
                    'go_to_adhd_triggers': '/adhd-relationships',
                    'go_to_partner_support': '/regulate/adhd-partner-support',
                    'go_to_infinity_loop': '/relationship-hub/infinity-loop',
                    // Body & audio tools
                    'go_to_body_scan': '/regulate/body-scan',
                    'go_to_pmr': '/regulate/pmr',
                    'go_to_somatic': '/regulate/somatic',
                    'go_to_guided_meditations': '/regulate/meditations',
                    'go_to_breathing_patterns': '/regulate/breathing',
                    'go_to_crisis': '/crisis',
                    // Specific breathing patterns
                    'go_to_breathing_physiological_sigh': '/regulate/breathing?pattern=sigh',
                    'go_to_breathing_478': '/regulate/breathing?pattern=478',
                    'go_to_breathing_box': '/regulate/breathing?pattern=box',
                    'go_to_breathing_extended_exhale': '/regulate/breathing?pattern=extended',
                    'go_to_breathing_coherent': '/regulate/breathing?pattern=coherent',
                };

                if (data.action && actionRoutes[data.action]) {
                    setTimeout(() => {
                        navigate(actionRoutes[data.action]);
                    }, 2000); // 2 second delay to read the message
                }
            } else {
                // Show a gentle fallback message
                setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: "I'm having a little trouble right now. Would you like to try one of the regulation tools?",
                    timestamp: new Date().toISOString()
                }]);
            }
        } catch (error) {
            console.error('Failed to send message:', error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: "Something went wrong. Let's take a breath and try again in a moment.",
                timestamp: new Date().toISOString()
            }]);
        } finally {
            setIsLoading(false);
            inputRef.current?.focus();
        }
    };

    const clearConversation = async () => {
        if (!sessionId) return;

        try {
            const token = localStorage.getItem('co_regulate_token');
            await fetch(`${API_URL}/api/guide/history/${sessionId}`, {
                method: 'DELETE',
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
        } catch (error) {
            console.error('Failed to clear history:', error);
        }

        // Create new session
        const newSession = crypto.randomUUID();
        setSessionId(newSession);
        localStorage.setItem('coregulate_guide_session', newSession);
        setMessages([]);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Quick prompts for users who don't know what to say
    const quickPrompts = [
        { text: "I'm feeling overwhelmed", emoji: "😔" },
        { text: "My mind won't stop racing", emoji: "🌀" },
        { text: "I need something quick", emoji: "⚡" },
        { text: "I'm shut down and numb", emoji: "😶" },
        { text: "Help me find the right tool", emoji: "🧭" }
    ];

    return (
        <div className="min-h-screen flex flex-col">
            {/* Header */}
            <header className="sticky top-0 z-40 glass-header">
                <Container size="lg" className="py-4">
                    <div className="flex items-center justify-between">
                        <TertiaryButton 
                            onClick={() => navigate('/home')}
                            className="gap-2"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Back
                        </TertiaryButton>
                        
                        <button
                            onClick={() => navigate('/home')}
                            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                            data-testid="header-logo"
                            aria-label="Go to dashboard"
                        >
                            <CoRegulateMark size={28} />
                            <Heading as="h1" size="text-lg" className="text-[#1D6B6B]">
                                Sage
                            </Heading>
                        </button>

                        <IconButton 
                            onClick={clearConversation}
                            variant="ghost"
                            title="Start fresh"
                        >
                            <Trash2 className="w-4 h-4 text-[#7D8E95]" />
                        </IconButton>
                    </div>
                </Container>
            </header>

            {/* Messages area */}
            <main className="flex-1 overflow-y-auto pb-48">
                <Container size="md" className="py-6">
                    {isLoadingHistory ? (
                        /* Loading history state */
                        <div className="text-center py-12">
                            <div className="w-16 h-16 rounded-full bg-[#7D8E95]/20 flex items-center justify-center mx-auto mb-6">
                                <div className="w-8 h-8 border-2 border-[#1D6B6B] border-t-transparent rounded-full animate-spin" />
                            </div>
                            <BodyText className="text-[#7D8E95]">
                                Loading your conversation...
                            </BodyText>
                        </div>
                    ) : messages.length === 0 ? (
                        /* Welcome state - just the greeting, quick prompts moved to bottom */
                        <div className="text-center py-12">
                            <div className="w-16 h-16 rounded-full bg-[#7D8E95]/20 flex items-center justify-center mx-auto mb-6">
                                <Sparkles className="w-8 h-8 text-[#1D6B6B]" />
                            </div>
                            <Heading as="h2" size="text-xl" className="text-[#1D6B6B] mb-3">
                                Hi, I'm Sage
                            </Heading>
                            <BodyText className="text-[#7D8E95] max-w-sm mx-auto">
                                I'm here to help you navigate CoRegulate. No pressure — we can go at your pace.
                            </BodyText>
                        </div>
                    ) : (
                        /* Chat messages */
                        <div className="space-y-4">
                            {messages.map((message, index) => {
                                // Parse message for action tags
                                const { text: cleanText, actions } = message.role === 'assistant' 
                                    ? parseMessageWithActions(message.content)
                                    : { text: message.content, actions: [] };
                                
                                return (
                                <div
                                    key={index}
                                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    {message.role === 'user' ? (
                                        /* User message - simple rounded bubble */
                                        <div className="max-w-[85%] rounded-2xl px-4 py-3 bg-[#7D8E95]/20 text-[#1D6B6B]">
                                            <BodyText className="text-[#1D6B6B] leading-relaxed whitespace-pre-wrap">
                                                {message.content}
                                            </BodyText>
                                        </div>
                                    ) : (
                                        /* Assistant message - speech bubble with tail on bottom-right */
                                        <div className="max-w-[85%] relative">
                                            <div className="rounded-2xl px-4 py-3 bg-white border border-white/40 text-[#1D6B6B] relative">
                                                <div className="flex items-center gap-1.5 mb-1">
                                                    <Sparkles className="w-3 h-3 text-[#1D6B6B]" />
                                                    <SmallText className="text-[#1D6B6B] font-medium">Sage</SmallText>
                                                </div>
                                                <BodyText className="text-[#1D6B6B] leading-relaxed whitespace-pre-wrap">
                                                    {cleanText}
                                                </BodyText>
                                                
                                                {/* Clickable tool links - subtle inline style */}
                                                {actions.length > 0 && (
                                                    <div className="mt-2 pt-2 border-t border-[#7D8E95]/10">
                                                        <SmallText className="text-[#7D8E95]/70 mb-1">Quick access:</SmallText>
                                                        <div className="flex flex-wrap gap-x-3 gap-y-1">
                                                            {actions.map((action, actionIndex) => (
                                                                <ToolLink 
                                                                    key={actionIndex}
                                                                    action={action}
                                                                    onClick={() => navigate(action.route)}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Show navigation indicator if this message has an action */}
                                                {message.action && index === messages.length - 1 && (
                                                    <div className="mt-3 flex items-center gap-2 text-[#1D6B6B]">
                                                        <div className="w-4 h-4 border-2 border-[#1D6B6B] border-t-transparent rounded-full animate-spin" />
                                                        <SmallText className="text-[#1D6B6B]">Taking you there...</SmallText>
                                                    </div>
                                                )}
                                            </div>
                                            {/* Speech bubble tail - bottom right */}
                                            <div 
                                                className="absolute -bottom-2 right-4 w-4 h-4 bg-white border-r border-b border-white/40 transform rotate-45"
                                                style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )})}
                            
                            {/* Loading indicator */}
                            {isLoading && (
                                <div className="flex justify-start">
                                    <div className="relative">
                                        <div className="bg-white border border-white/40 rounded-2xl px-4 py-3">
                                            <div className="flex items-center gap-1.5 mb-1">
                                                <Sparkles className="w-3 h-3 text-[#1D6B6B]" />
                                                <SmallText className="text-[#1D6B6B] font-medium">Sage</SmallText>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <div className="w-2 h-2 rounded-full bg-[#7D8E95] animate-pulse" style={{ animationDelay: '0ms' }} />
                                                <div className="w-2 h-2 rounded-full bg-[#7D8E95] animate-pulse" style={{ animationDelay: '150ms' }} />
                                                <div className="w-2 h-2 rounded-full bg-[#7D8E95] animate-pulse" style={{ animationDelay: '300ms' }} />
                                            </div>
                                        </div>
                                        {/* Speech bubble tail */}
                                        <div 
                                            className="absolute -bottom-2 right-4 w-4 h-4 bg-white border-r border-b border-white/40 transform rotate-45"
                                            style={{ clipPath: 'polygon(100% 0, 100% 100%, 0 100%)' }}
                                        />
                                    </div>
                                </div>
                            )}
                            
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </Container>
            </main>

            {/* Input area - fixed at bottom with quick prompts underneath */}
            <div className="fixed bottom-0 left-0 right-0 glass-header p-4">
                <Container size="md">
                    {/* Main input row */}
                    <div className="flex items-end gap-3">
                        <div className="flex-1 relative">
                            <textarea
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyPress}
                                placeholder="Type a message..."
                                rows={1}
                                className="w-full px-4 py-3 pr-12 rounded-2xl border border-white/40 bg-white resize-none focus:outline-none focus:border-[#7D8E95] text-[#1D6B6B] placeholder-[#7D8E95]"
                                style={{ maxHeight: '120px' }}
                                disabled={isLoading}
                            />
                        </div>
                        <button
                            onClick={sendMessage}
                            disabled={!input.trim() || isLoading}
                            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                                input.trim() && !isLoading
                                    ? 'bg-[#1D6B6B] text-white hover:bg-[#1D6B6B]'
                                    : 'bg-[#FFBB98] text-[#7D8E95]'
                            }`}
                        >
                            <Send className="w-5 h-5" />
                        </button>
                    </div>
                    
                    {/* Quick prompts - Pill/Chip Design */}
                    <div className="mt-3 overflow-x-auto pb-2 -mx-2 px-2 scrollbar-hide">
                        <div className="flex gap-2 w-max">
                            {quickPrompts.map((prompt, index) => {
                                const iconColors = [
                                    'bg-gradient-to-br from-[#FFBB98] to-[#E09060]', // Coral
                                    'bg-gradient-to-br from-[#2A8585] to-[#1D6B6B]', // Teal
                                    'bg-gradient-to-br from-[#E8A0A0] to-[#C97B7B]', // Dusty Rose
                                    'bg-gradient-to-br from-[#9AABB8] to-[#7D8E95]', // Neutral
                                    'bg-gradient-to-br from-[#2A8585] to-[#1D6B6B]'  // Teal
                                ];
                                return (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            setInput(prompt.text);
                                            inputRef.current?.focus();
                                        }}
                                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/70 hover:bg-white/90 backdrop-blur-sm border border-white/60 hover:border-[#2A8585]/30 text-[#1D6B6B] text-xs font-medium transition-all duration-300 hover:shadow-md active:scale-95 whitespace-nowrap flex-shrink-0"
                                        data-testid={`quick-prompt-${index}`}
                                    >
                                        <span className={`w-5 h-5 rounded-md ${iconColors[index]} flex items-center justify-center text-white text-[10px]`}>
                                            {prompt.emoji}
                                        </span>
                                        {prompt.text}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    
                    <SmallText className="text-[#7D8E95] text-center mt-2 block text-xs">
                        Sage supports navigation, not therapy. For deeper support, reach out to a human.
                    </SmallText>
                </Container>
            </div>
        </div>
    );
};

export default AIGuide;
